import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { faFileInvoiceDollar, faSave, faPlus, faFilePdf, faTrash, faLink } from "@fortawesome/pro-regular-svg-icons";
import { toast } from "react-toastify";
import NotificationToast from "../../components/Helpers/NotificationToast";
import { Formik, Form, Field } from "formik";
import SubmitButton from "../Forms/SubmitButton";
import CustomButton from "../Helpers/CustomButton";
import QuickFormSelect from "../Forms/QuickFormSelect";
import QuickFormText from "../Forms/QuickFormText";
import LoadingComponent from "../Helpers/LoadingComponent";
import { getBooking } from "../../store/booking/bookingActions";

function AosSigningCountdown({ expiresAt }) {
    const calcTime = () => {
        const diff = new Date(expiresAt) - Date.now();
        if (diff <= 0) return { expired: true };
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return { expired: false, hours, minutes };
    };
    
    const [time, setTime] = useState(calcTime);

    useEffect(() => {
        const interval = setInterval(() => {
            setTime(calcTime());
        }, 60000);
        return () => clearInterval(interval);
    }, [expiresAt]);

    if (time.expired) {
        return <span className="text-red-600 font-semibold">⚠️ AOS Signing Expired</span>;
    }
    
    if (time.hours < 24) {
        return <span className="text-amber-600 font-semibold">⏱️ {time.hours}h {time.minutes}m to sign AOS</span>;
    }
    
    const days = Math.floor(time.hours / 24);
    const remainingHours = time.hours % 24;
    return <span className="text-blue-600 font-semibold">⏱️ {days}d {remainingHours}h to sign AOS</span>;
}

export default function GridInvoiceInformation({ setIgnoreRefresh , isShortlist = false}) {
    const dispatch = useDispatch();
    const [booking, setBooking] = useState([]);
    const { current_booking } = useSelector((state) => state.booking);
    const { current_staff } = useSelector((state) => state.auth);
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [aosRecords, setAosRecords] = useState([]);
    const [aosButtonLoading, setaosButtonLoading] = useState(false);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [linkedChats, setLinkedChats] = useState([]);
    const [salesExclusiveAccess, setSalesExclusiveAccess] = useState(false);
    const [websiteExclusiveAccess, setWebsiteExclusiveAccess] = useState(false);
    const [toggleLoading, setToggleLoading] = useState(false);
    const [websiteToggleLoading, setWebsiteToggleLoading] = useState(false);
    const [expandedItems, setExpandedItems] = useState({});
    const [paymentLinkLoading, setPaymentLinkLoading] = useState({});
    var aosButtonLoadingText = "Generating AOS";

    useEffect(() => {
        getLocalBooking();
        // Fetch properties directly
        axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}properties`,
        })
            .then((response) => {
                setProperties(response.data);
            })
            .catch((error) => {
                console.error("Failed to fetch properties:", error);
            });
        getAosRecords(); // Fetch AOS records
        
        // Set initial sales exclusive access state
        if (current_booking?.sales_exclusive_access !== undefined) {
            setSalesExclusiveAccess(current_booking.sales_exclusive_access);
        }
        
        // Set initial website exclusive access state
        if (current_booking?.website_exclusive_access !== undefined) {
            setWebsiteExclusiveAccess(current_booking.website_exclusive_access);
        }
    }, [current_booking?.sales_exclusive_access, current_booking?.website_exclusive_access]);

    // When properties are loaded, select the first one and load its units
    useEffect(() => {
        if (properties && properties.length > 0) {
            const firstProperty = properties[0];
            setSelectedProperty(firstProperty);
            getUnits(firstProperty.value);
        }
    }, [properties]);
    // Filter AOS records based on isShortlist prop
    useEffect(() => {
        if (aosRecords) {
            const filtered = isShortlist 
                ? aosRecords.filter(record => record.status === 'shortlisted')
                : aosRecords.filter(record => record.status !== 'shortlisted');
            setFilteredRecords(filtered);
        }
    }, [aosRecords, isShortlist]);
    // Handle property change
    const handlePropertyChange = (e) => {
        const propertyValue = e.target.value;
        const property = properties.find((p) => p.value == propertyValue);
        if (property) {
            setSelectedProperty(property);
            getUnits(property.value);
        }
    };

    async function getLocalBooking() {
        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}user_profile/${current_booking.user.id}`,
        }).then((response) => {
            setBooking(response.data);
            setLoading(false);
        });
    }

    async function getUnits(id) {
        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}units/${id}`,
        }).then((response) => {
            setUnits(response.data);
        });
    }

    // Function to get the AOS records
    async function getAosRecords() {
        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}aos_records/${current_booking.id}`, // Assuming there's an endpoint for AOS records
        })
            .then((response) => {
                // Handle new response structure with aos_records and linked_chats
                if (response.data.aos_records) {
                    setAosRecords(response.data.aos_records);
                    setLinkedChats(response.data.linked_chats || []);
                } else {
                    // Fallback for old response format
                    setAosRecords(response.data);
                    setLinkedChats([]);
                }
            })
            .catch(() => {
                toast(<NotificationToast title="Failed to retrieve AOS records" message="Please try again." type="error" />);
            });
    }

    async function generatePaymentLink(aosId) {
        setPaymentLinkLoading(prev => ({ ...prev, [aosId]: true }));

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}aos_generate_payment_link`, {
                aos_id: aosId,
            });

            if (response.data.success) {
                const paymentLink = response.data.payment_link;
                
                // Copy to clipboard
                await navigator.clipboard.writeText(paymentLink);
                
                toast(<NotificationToast 
                    title="Payment Link Generated" 
                    message={`Link copied to clipboard! Amount: R${parseFloat(response.data.deposit_amount).toLocaleString()}`} 
                />);
            } else {
                toast(<NotificationToast title="Error" message={response.data.message || "Failed to generate payment link"} type="error" />);
            }
        } catch (error) {
            console.error('Payment link generation error:', error);
            toast(<NotificationToast 
                title="Error" 
                message={error.response?.data?.message || "Failed to generate payment link"} 
                type="error" 
            />);
        } finally {
            setPaymentLinkLoading(prev => ({ ...prev, [aosId]: false }));
        }
    }

    async function addNewAosRecord() {
        await axios({
            method: "put",
            url: `${process.env.REACT_APP_API_URL}aos_create`,
            data: {
                booking_id: current_booking.id,
                status: isShortlist ? 'shortlisted' : 'pending',
            },
        })
            .then((response) => {
                getAosRecords();
                toast(<NotificationToast title="AOS Record Added" message="A new AOS record has been successfully added." />);
            })
            .catch((error) => {
                toast(<NotificationToast title="Error" message="Failed to add a new AOS record." type="error" />);
            });
    }

    async function generate_aos(recordId) {
        setaosButtonLoading(true);

        axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}generate_aos/${recordId}`,
        })
            .then((response) => {
                getAosRecords();
                
                // Check if signing URL was returned
                if (response.data.aos_signing_url) {
                    const signingUrl = response.data.aos_signing_url;
                    
                    // Create custom toast with copy button
                    toast(
                        <div>
                            <div className="font-semibold mb-2">AOS Generated Successfully!</div>
                            <div className="text-sm mb-2">Signing link:</div>
                            <div className="flex items-center gap-2">
                                <input 
                                    type="text" 
                                    value={signingUrl} 
                                    readOnly 
                                    className="flex-1 px-2 py-1 text-xs border rounded bg-gray-50"
                                    onClick={(e) => e.target.select()}
                                />
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(signingUrl);
                                        toast(<NotificationToast title={"Copied!"} message={"Signing link copied to clipboard"} />);
                                    }}
                                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                >
                                    Copy
                                </button>
                            </div>
                        </div>,
                        { autoClose: 10000 }
                    );
                } else {
                    toast(<NotificationToast title={"AOS generated"} message={"You can now send the link to the applicant."} />);
                }
            })
            .catch((response) => {
                toast(<NotificationToast title={"AOS failed"} message={response.response.data} list={true} type="error" />);
            })
            .finally(() => {
                setaosButtonLoading(false);
            });
    }

    async function download_file(file) {
        setLoading(true);
        await axios({
            method: "get",
            responseType: "blob",
            url: `${process.env.REACT_APP_API_URL}download_file/${file.id}`,
        })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", file.name + "." + file.extension);
                document.body.appendChild(link);
                link.click();
            })
            .catch(function (error) {
                toast(<NotificationToast title="File Missing" message={"Something went wrong, we could not download this file!"} type="error" />);
            })
            .finally(() => setLoading(false));
    }

    async function delete_aos(id) {
        if (window.confirm("Are you sure you want to delete this AOS?")) {
            await axios({
                method: "delete",
                url: `${process.env.REACT_APP_API_URL}aos_delete/${id}`,
            })
                .then((response) => {
                    getAosRecords();
                    toast(<NotificationToast title={"AOS deleted"} message={"The AOS record has been deleted."} />);
                })
                .catch((response) => {
                    toast(<NotificationToast title={"AOS delete failed"} message={response.response.data} list={true} type="error" />);
                });
        }
    }

    async function toggleSalesExclusiveAccess() {
        setToggleLoading(true);
        
        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}toggle_sales_exclusive_access`,
            data: {
                booking_id: current_booking.id,
            },
        })
            .then((response) => {
                setSalesExclusiveAccess(response.data.sales_exclusive_access);
                toast(
                    <NotificationToast
                        title={response.data.sales_exclusive_access ? "Access Granted" : "Access Revoked"}
                        message={response.data.sales_exclusive_access ? "User now has exclusive access to price list platform" : "User no longer has price list platform access"}
                    />
                );
                dispatch(getBooking(current_booking.id));
            })
            .catch((error) => {
                toast(<NotificationToast title="Error" message="Failed to update price list platform access" type="error" />);
            })
            .finally(() => {
                setToggleLoading(false);
            });
    }

    async function toggleWebsiteExclusiveAccess() {
        setWebsiteToggleLoading(true);
        
        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}toggle_website_exclusive_access`,
            data: {
                booking_id: current_booking.id,
            },
        })
            .then((response) => {
                setWebsiteExclusiveAccess(response.data.website_exclusive_access);
                toast(
                    <NotificationToast
                        title={response.data.website_exclusive_access ? "Website Access Granted" : "Website Access Revoked"}
                        message={response.data.website_exclusive_access ? "User now has exclusive access to website" : "User no longer has website access"}
                    />
                );
                dispatch(getBooking(current_booking.id));
            })
            .catch((error) => {
                toast(<NotificationToast title="Error" message="Failed to update website access" type="error" />);
            })
            .finally(() => {
                setWebsiteToggleLoading(false);
            });
    }

    if (loading) {
        return <LoadingComponent />;
    }

    const statusOptions = [
        { value: "pending", label: "Pending" },
        { value: "pending_payment", label: "Pending Payment" },
        { value: "reserved", label: "Reserved" },
        { value: "signed", label: "Signed" },
        { value: "cancelled", label: "Cancelled" },
        { value: "shortlisted", label: "Shortlisted" },
        { value: "waitlisted", label: "Waitlisted" },
    ];

    return (
        <div className="scroll-smooth">
            {/* Exclusive Access Toggles */}
            {isShortlist && (
                <div className="mb-4 space-y-3">
                    {/* Website Access */}
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-semibold text-gray-900">Website Access</h3>
                                <p className="text-sm text-gray-600">
                                    {websiteExclusiveAccess ? 'User has exclusive access to WordPress website' : 'User does not have website access'}
                                </p>
                            </div>
                            <button
                                onClick={toggleWebsiteExclusiveAccess}
                                disabled={websiteToggleLoading}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    websiteExclusiveAccess ? 'bg-purple-600' : 'bg-gray-200'
                                } ${websiteToggleLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        websiteExclusiveAccess ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                        </div>
                    </div>

                    {/* Price List Platform Access */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-semibold text-gray-900">Price List Platform Access</h3>
                                <p className="text-sm text-gray-600">
                                    {salesExclusiveAccess ? 'User has exclusive access to Romi-Sales price list platform' : 'User does not have price list platform access'}
                                </p>
                            </div>
                            <button
                                onClick={toggleSalesExclusiveAccess}
                                disabled={toggleLoading}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    salesExclusiveAccess ? 'bg-blue-600' : 'bg-gray-200'
                                } ${toggleLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        salesExclusiveAccess ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* AOS Records Display */}
            {filteredRecords.length > 0 ? (
                <div className="space-y-2">
                    {filteredRecords.map((record, index) => {
                        const isExpanded = expandedItems[record.id];
                        const unitName = record.unit?.name || 'No unit chosen';
                        const statusLabel = statusOptions.find(s => s.value === record.status)?.label || record.status;
                        
                        return (
                        <div key={index} className="border rounded-lg overflow-hidden">
                            {/* Compact Header */}
                            <div 
                                className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                                onClick={() => setExpandedItems(prev => ({ ...prev, [record.id]: !prev[record.id] }))}
                            >
                                <div className="flex items-center gap-3 flex-1 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900">{unitName}</span>
                                    <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">{statusLabel}</span>
                                    {record.deposit_amount && (
                                        <span className="text-xs text-gray-600">R{parseFloat(record.deposit_amount).toLocaleString()}</span>
                                    )}
                                    {record.aos_signing_expires_at && (record.status === 'paid' || record.status === 'reserved') && (
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                            new Date(record.aos_signing_expires_at) < new Date() 
                                                ? 'bg-red-100 text-red-800' 
                                                : 'bg-amber-100 text-amber-800'
                                        }`}>
                                            {new Date(record.aos_signing_expires_at) < new Date() 
                                                ? '⚠️ AOS Expired' 
                                                : `⏱️ AOS Expires: ${new Date(record.aos_signing_expires_at).toLocaleString('en-ZA', { 
                                                    month: 'short', 
                                                    day: 'numeric', 
                                                    hour: '2-digit', 
                                                    minute: '2-digit' 
                                                })}`
                                            }
                                        </span>
                                    )}
                                </div>
                                <svg className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>

                            {/* Expanded Content */}
                            {isExpanded && (
                            <div className="p-4 bg-white border-t">
                                {/* AOS Signing Deadline Banner */}
                                {record.aos_signing_expires_at && (
                                    <div className="mb-4 p-4 rounded-lg border-2 border-blue-200 bg-blue-50">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h4 className="font-semibold text-blue-900 mb-1">AOS Signing Deadline</h4>
                                                <p className="text-sm text-blue-700">
                                                    Expires: {new Date(record.aos_signing_expires_at).toLocaleString('en-ZA', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <AosSigningCountdown expiresAt={record.aos_signing_expires_at} />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            <Formik
                                enableReinitialize
                                initialValues={{
                                    id: record.id,
                                    unit_id: record.unit_id,
                                    status: record.status,
                                    notes: record.notes || '',
                                    parking_bay: record.parking_bay,
                                    store_no: record.store_no,
                                    exclusive_use: record.exclusive_use,
                                    owner_booking_id: record.owner_booking_id || '',
                                    deposit_amount: record.deposit_amount ? parseFloat(record.deposit_amount).toString() : '',
                                    aos_signing_expires_at: record.aos_signing_expires_at ? new Date(record.aos_signing_expires_at).toISOString().slice(0, 16) : '',
                                }}
                                onSubmit={async (values, { setSubmitting }) => {
                                    let submitData = {
                                        id: record.id,
                                        unit_id: values.unit_id,
                                        status: values.status,
                                        notes: values.notes,
                                        booking_id: current_booking.id,
                                        parking_bay: values.parking_bay,
                                        store_no: values.store_no,
                                        exclusive_use: values.exclusive_use,
                                        owner_booking_id: values.owner_booking_id || null,
                                        deposit_amount: values.deposit_amount || null,
                                        aos_signing_expires_at: values.aos_signing_expires_at || null,
                                    };

                                    await axios({
                                        method: "put",
                                        url: `${process.env.REACT_APP_API_URL}aos_save`, // Assuming there's an endpoint for AOS save
                                        data: submitData,
                                    })
                                        .then(() => {
                                            getAosRecords();
                                            toast(<NotificationToast title="Success" message="Data has been updated successfully" />);
                                        })
                                        .catch((error) => {
                                            toast(<NotificationToast title="Something went wrong" message="" type="error" />);
                                        });

                                    setSubmitting(false);
                                }}                    
                            >
                                {({ values, errors, handleSubmit, setFieldValue, setFieldTouched, isSubmitting, isValid, dirty }) => (
                                    <Form onSubmit={handleSubmit} className="rounded-3xl">
                                        <div className="pb-6 grid gap-x-2 gap-y-8 grid-cols-2">
                                            {(process.env.REACT_APP_COLOUR === "QL" || process.env.REACT_APP_COLOUR === "QP") && (
                                                <>
                                                    {properties.length > 1 && (
                                                        <div className="col-span-2">
                                                            <div className="form-group">
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
                                                                <select
                                                                    value={selectedProperty?.value || ""}
                                                                    onChange={handlePropertyChange}
                                                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                                                    disabled={current_staff?.hideAppInfoUpdate == 1}
                                                                >
                                                                    {properties.map((property) => (
                                                                        <option key={property.value} value={property.value}>
                                                                            {property.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <QuickFormSelect
                                                        label="Unit"
                                                        name="unit_id"
                                                        onChange={setFieldValue}
                                                        onBlur={setFieldTouched}
                                                        value={values.unit_id}
                                                        error={errors.unit_id}
                                                        options={units.length > 0 ? units : "none"}
                                                        disabled={current_staff?.hideAppInfoUpdate == 1 ? true : false}
                                                    />

                                                    <QuickFormSelect
                                                        label="Status"
                                                        name="status"
                                                        onChange={setFieldValue}
                                                        onBlur={setFieldTouched}
                                                        value={values.status}
                                                        error={errors.status}
                                                        options={statusOptions.length > 0 ? statusOptions : "none"}
                                                        disabled={current_staff?.hideAppInfoUpdate == 1 ? true : false}
                                                    />

                                                    <div className="flex flex-col">
                                                        <label className="text-sm text-dark_background mb-1">Deposit Amount (R)</label>
                                                        <Field
                                                            name="deposit_amount"
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="e.g. 5000"
                                                            disabled={current_staff?.hideAppInfoUpdate == 1}
                                                            className="rounded-lg py-2 pl-2 truncate font-light text-sm focus:outline-none focus:ring-primary focus:border-primary border"
                                                        />
                                                    </div>

                                                    <div className="flex flex-col">
                                                        <label className="text-sm text-dark_background mb-1">AOS Signing Expires At</label>
                                                        <Field
                                                            name="aos_signing_expires_at"
                                                            type="datetime-local"
                                                            placeholder="Set signing deadline"
                                                            disabled={current_staff?.hideAppInfoUpdate == 1}
                                                            className="rounded-lg py-2 pl-2 truncate font-light text-sm focus:outline-none focus:ring-primary focus:border-primary border"
                                                        />
                                                    </div>

                                                    <div className="col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                                        <Field
                                                            as="textarea"
                                                            name="notes"
                                                            rows="3"
                                                            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
                                                            placeholder="Add notes about this unit..."
                                                            disabled={current_staff?.hideAppInfoUpdate == 1}
                                                        />
                                                    </div>

                                                    {!isShortlist && (
                                                        <>
                                                    <Field name="parking_bay" as={QuickFormText} label="Parking Bay" />
                                                    <Field name="store_no" as={QuickFormText} label="Store No" />
                                                    <Field name="exclusive_use" as={QuickFormText} label="Exclusive Use" />

                                                    <div className="col-span-2 space-y-1 text-sm text-gray-700">
                                                        <div><span className="font-medium">Unit Type:</span> {units?.find((unit) => unit.id == values.unit_id)?.unit_type_name ?? "TBC"}</div>
                                                        <div><span className="font-medium">Price:</span> {units?.find((unit) => unit.id == values.unit_id)?.price ?? "TBC"}</div>
                                                        <div><span className="font-medium">Launch Price:</span> {units?.find((unit) => unit.id == values.unit_id)?.launch_day_price ?? "TBC"}</div>
                                                        <div><span className="font-medium">Internal Area:</span> {units?.find((unit) => unit.id == values.unit_id)?.area ?? "TBC"}</div>
                                                        <div><span className="font-medium">External Area:</span> {units?.find((unit) => unit.id == values.unit_id)?.area_external ?? "TBC"}</div>
                                                    </div>
                                                    </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                               
                                        {linkedChats.length > 0 && (process.env.REACT_APP_COLOUR === "QL" || process.env.REACT_APP_COLOUR === "QP") && (
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Owner (Linked Chat)</label>
                                                <select
                                                    name="owner_booking_id"
                                                    value={values.owner_booking_id || ''}
                                                    onChange={(e) => setFieldValue('owner_booking_id', e.target.value || null)}
                                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                                    disabled={current_staff?.hideAppInfoUpdate == 1}
                                                >
                                                    <option value="">Select Owner</option>
                                                    {linkedChats.map((chat) => (
                                                        <option key={chat.id} value={chat.id}>
                                                            {chat.user_name} ({chat.booking_reference}) - {chat.linked_type}
                                                        </option>
                                                    ))}
                                                </select>
                                                <p className="mt-1 text-xs text-gray-500">Select which linked chat owns this AOS</p>
                                            </div>
                                        )}
                           
                                        {!current_staff.building_manager && current_staff?.hideAppInfoUpdate != 1 && (
                                            <SubmitButton name="Save" disabled={!isValid || isSubmitting || !dirty} isSubmitting={isSubmitting} icon={faSave} text="Update" style={{ width: "100%" }} />
                                        )}

                                        {record.unit_id > 0 && !isShortlist && (
                                            <CustomButton
                                                text="Generate AOS"
                                                styling="w-full mt-4"
                                                icon={faFileInvoiceDollar}
                                                onClick={() => generate_aos(record.id)}
                                                loading={aosButtonLoading ? 1 : 0}
                                                loadingtext={aosButtonLoadingText}
                                            />
                                        )}

                                        {record.unit_id > 0 && (record.status === 'pending_payment' || record.status === 'reserved') && (
                                            <CustomButton
                                                text="Generate Payment Link"
                                                styling="w-full mt-4 bg-green-600"
                                                icon={faLink}
                                                onClick={() => generatePaymentLink(record.id)}
                                                loading={paymentLinkLoading[record.id] ? 1 : 0}
                                                loadingtext="Generating Link..."
                                            />
                                        )}

                                        {record.file?.id && <CustomButton text="Download AOS PDF" styling="w-full mt-4" icon={faFilePdf} onClick={() => download_file(record.file)} />}

                                        {!current_staff.building_manager && current_staff?.hideAppInfoUpdate != 1 && (
                                            <CustomButton text="Delete AOS" styling="w-full mt-4 bg-red-500" icon={faTrash} onClick={() => delete_aos(record.id)} />
                                        )}
                                    </Form>
                                )}
                            </Formik>
                            </div>
                            )}
                        </div>
                        );
                    })}
                </div>
            ) : (
                //if isShortlist is true, show shortlisted message
                isShortlist ? (
                    <div className="text-center py-4 text-gray-500">
                        No shortlisted AOS records found
                    </div>
                ) : (
                    <div className="text-center py-4 text-gray-500">
                        No AOS records found
                    </div>
                )
            )}

            {/* Add New AOS Button */}
            {!current_staff.building_manager && current_staff?.hideAppInfoUpdate != 1 && <CustomButton text="Add AOS Record" styling="w-full mt-4" icon={faPlus} onClick={() => addNewAosRecord()} />}
        </div>
    );
}
